import { z } from 'zod';
import { COST_CATEGORIES, ALLOCATION_KEYS, EVENT_TYPES, RENT_CHANGE_CHANNELS, RENT_CHANGE_KINDS, TENANCY_KINDS } from '../enums';
import { RENT_CHANGE_FIELDS } from '../rentChange';
import { zAddress, zCents, zDate, zId, zReason } from './common';
import { SALUTATIONS } from '../persons';
import { HANDOVER_KEY_TYPES } from '../handover';
import { roomsMatchArea, zAreaRooms } from './properties';

const personName = z.string().trim().min(1).max(120);
const namePart = z.string().trim().max(80);
/** Optional master data of a person (Eckdaten). `name` is derived from first + last name when those are given. */
const personDetails = {
  salutation: z.enum(SALUTATIONS),
  firstName: namePart,
  lastName: namePart,
  email: z.string().trim().email(),
  phone: z.string().trim().max(40),
  mobile: z.string().trim().max(40),
  birthDate: zDate,
  previousAddress: zAddress,
  notes: z.string().trim().max(2000),
};
const hasName = (b: { name?: string | null; firstName?: string | null; lastName?: string | null }) => !!(b.name?.trim() || b.firstName?.trim() || b.lastName?.trim());
export const CreatePersonBody = z
  .object({
    name: personName.optional(),
    salutation: personDetails.salutation.optional(),
    firstName: personDetails.firstName.optional(),
    lastName: personDetails.lastName.optional(),
    email: personDetails.email.optional(),
    phone: personDetails.phone.optional(),
    mobile: personDetails.mobile.optional(),
    birthDate: personDetails.birthDate.optional(),
    postalAddress: zAddress.optional(),
    previousAddress: personDetails.previousAddress.optional(),
    notes: personDetails.notes.optional(),
    ibans: z.array(z.string().max(34)).max(5).optional(),
  })
  .refine(hasName, { message: 'Name fehlt', path: ['lastName'] });
/**
 * `null` removes an optional field. `postalAddress` only sets the first address of a person without one; later
 * moves are `person.address_changed` events (the address is a time slice, its history is never overwritten).
 */
export const UpdatePersonBody = z.object({
  name: personName.optional(),
  salutation: personDetails.salutation.nullable().optional(),
  firstName: personDetails.firstName.nullable().optional(),
  lastName: personDetails.lastName.nullable().optional(),
  email: personDetails.email.nullable().optional(),
  phone: personDetails.phone.nullable().optional(),
  mobile: personDetails.mobile.nullable().optional(),
  birthDate: personDetails.birthDate.nullable().optional(),
  postalAddress: zAddress.optional(),
  previousAddress: personDetails.previousAddress.nullable().optional(),
  notes: personDetails.notes.nullable().optional(),
  ibans: z.array(z.string().max(34)).max(5).optional(),
});

/** One row of handed-over keys; `label` only for free-text keys (type `other`). */
export const zHandoverKey = z.object({ type: z.enum(HANDOVER_KEY_TYPES), label: z.string().trim().max(60).optional(), count: z.number().int().min(1).max(99) });
const occupants = z.object({ adults: z.number().int().min(0).max(30), children: z.number().int().min(0).max(30).default(0), babies: z.number().int().min(0).max(30).default(0) });
export const CreateTenancyBody = z.object({
  unitId: zId,
  start: zDate,
  end: zDate.nullable().default(null),
  contractNo: z.string().max(40).optional(),
  kind: z.enum(TENANCY_KINDS).default('residential'),
  partyIds: z.array(zId).min(1, { message: 'Mindestens eine Vertragspartei' }),
  occupants,
  rentColdCents: zCents.min(0),
  prepaymentOpsCents: zCents.min(0).default(0),
  prepaymentHeatCents: zCents.min(0).default(0),
  isFlatRate: z.boolean().default(false),
  keyOverrides: z.partialRecord(z.enum(COST_CATEGORIES), z.enum(ALLOCATION_KEYS)).default({}),
  excludedCategories: z.array(z.enum(COST_CATEGORIES)).default([]),
  /** Own "sonstige Betriebskosten" types the lease does not allow (ids of property.customCostTypes, docs/15 #36). */
  excludedCustomTypes: z.array(z.string().trim().min(1).max(40)).max(30).default([]),
  /** Commercial only: landlord opted for VAT (§ 9 UStG); statements then carry a hint (docs/15 #46, ADR-0022). */
  vatOpted: z.boolean().optional(),
  emailConsent: z.boolean().default(false),
  tenantPortal: z.object({ enabled: z.boolean().default(true), showPayments: z.boolean().default(false) }).default({ enabled: true, showPayments: false }),
  dueDay: z.number().int().min(1).max(28).optional(),
  /**
   * Kaution as agreed in the contract. With `receivedOn` it was already paid: the server records
   * tenancy.deposit_received right after the start (same as POST /tenancies/:id/deposit).
   */
  deposit: z.object({ amountCents: zCents.positive(), type: z.enum(['cash', 'account', 'guarantee']).default('account'), receivedOn: zDate.optional(), instalments: z.union([z.literal(1), z.literal(3)]).optional() }).optional(),
  /**
   * The start lies before the unit's "Besteht seit": move the unit back to the start in the same transaction
   * (event `unit.backdated`, docs/15 #23). Without it the unit keeps its date (old behaviour).
   */
  backdateUnit: z.boolean().default(false),
  /**
   * docs/15 #38 "Soll ab … nachbuchen?": the current month is charged right away; with a date before it, the months
   * from that date (not before the start) are charged as well. Absent/null: only the current month.
   */
  chargesFrom: zDate.nullable().optional(),
});
export const UpdateTenancyBody = z.object({
  contractNo: z.string().max(40).nullable().optional(),
  isFlatRate: z.boolean().optional(),
  keyOverrides: z.partialRecord(z.enum(COST_CATEGORIES), z.enum(ALLOCATION_KEYS)).optional(),
  excludedCategories: z.array(z.enum(COST_CATEGORIES)).optional(),
  excludedCustomTypes: z.array(z.string().trim().min(1).max(40)).max(30).optional(),
  vatOpted: z.boolean().optional(),
  emailConsent: z.boolean().optional(),
  tenantPortal: z.object({ enabled: z.boolean().optional(), showPayments: z.boolean().optional() }).optional(),
  dueDay: z.number().int().min(1).max(28).nullable().optional(),
});
/** Mietverhältnis in eine andere Wohnung verschieben (correction of a tenancy recorded in the wrong unit). */
export const MoveTenancyBody = z.object({ unitId: zId });

/** Payloads per event type (Zod je type, 04-datenmodell). */
/**
 * Grounds of a change of contract parties (checked against the statutes on 18.10.2026, docs/19):
 * release = Entlassung by agreement of landlord and all tenants (no statute; BGH VIII ZR 14/04), joined = new party by
 * agreement, divorce_1568a = § 1568a Abs. 3 BGB (notice of both spouses to the landlord), succession_563 = § 563 BGB
 * (spouse/partner/children/household members enter), survivors_563a = § 563a BGB (continues with the surviving
 * tenants), heirs_564 = § 564 BGB (heirs), other.
 */
export const PARTY_CHANGE_REASONS = ['release', 'joined', 'divorce_1568a', 'succession_563', 'survivors_563a', 'heirs_564', 'other'] as const;
export type PartyChangeReason = (typeof PARTY_CHANGE_REASONS)[number];
export const EventPayloads = {
  'unit.area_changed': z.object({ area: z.number().positive(), rooms: zAreaRooms.optional() }).refine((v) => !v.rooms || roomsMatchArea(v.rooms, v.area), { message: 'Raumliste ergibt eine andere Fläche', path: ['rooms'] }),
  'unit.heating_area_changed': z.object({ heatingArea: z.number().positive() }),
  'unit.status_changed': z.object({ status: z.enum(['rentable', 'owner_occupied', 'renovation', 'not_rentable']) }),
  'unit.merged': z.object({ unitIds: z.array(zId).min(2), label: z.string().min(1).max(60), area: z.number().positive() }),
  'unit.split': z.object({ parts: z.array(z.object({ label: z.string().min(1).max(60), area: z.number().positive() })).min(2) }),
  'property.total_area_changed': z.object({ area: z.number().positive() }),
  'tenancy.notice_given': z.object({ endDate: zDate.optional() }),
  'tenancy.ended': z.object({}),
  // why a contract party changes (docs/18 V9, docs/19 Rechtsgrundlagen): kept on the event for the file and letters
  'tenancy.party_added': z.object({ personId: zId, reason: z.enum(PARTY_CHANGE_REASONS).optional() }),
  'tenancy.party_removed': z.object({ personId: zId, reason: z.enum(PARTY_CHANGE_REASONS).optional() }),
  'tenancy.occupants_changed': occupants,
  'tenancy.rent_changed': z.object({ rentColdCents: zCents.min(0) }),
  'tenancy.prepayment_changed': z.object({ prepaymentOpsCents: zCents.min(0).optional(), prepaymentHeatCents: zCents.min(0).optional() }).refine((v) => v.prepaymentOpsCents !== undefined || v.prepaymentHeatCents !== undefined, { message: 'Mindestens ein Betrag' }),
  'tenancy.deposit_received': z.object({ amountCents: zCents.positive(), type: z.enum(['cash', 'account', 'guarantee']), accountIban: z.string().max(34).optional() }),
  'person.address_changed': z.object({ address: zAddress }),
  /** `knownOn`: the day the landlord learned of the death (the one-month periods of §§ 563, 563a, 564 BGB run from knowledge). */
  'person.deceased': z.object({ knownOn: zDate.optional(), tenancyId: zId.optional(), continuation: z.enum(['others', 'family', 'heirs']).optional() }),
  'meter.removed': z.object({ finalValue: z.number().min(0) }),
  'handover.protocol': z.object({ tenancyId: zId, direction: z.enum(['move_in', 'move_out']), readings: z.array(z.object({ meterId: zId, value: z.number().min(0) })).default([]), keys: z.union([z.array(zHandoverKey).max(30), z.number().int().min(0)]).optional(), notes: z.string().max(5000).optional(), photoIds: z.array(zId).default([]), handoverId: zId.optional() }),
} as const;
export type UserEventType = keyof typeof EventPayloads;
export const USER_EVENT_TYPES = Object.keys(EventPayloads) as UserEventType[];

export const EventBody = z.object({
  type: z.enum(EVENT_TYPES).refine((t): t is UserEventType => t in EventPayloads, { message: 'Dieses Ereignis kann nicht direkt erfasst werden' }),
  entityId: zId,
  effectiveDate: zDate,
  payload: z.record(z.string(), z.unknown()).default({}),
  reason: z.string().max(500).optional(),
});
export const CorrectEventBody = z.object({ effectiveDate: zDate.optional(), payload: z.record(z.string(), z.unknown()).optional(), reason: zReason, cancel: z.boolean().default(false) });
export const RentAdjustmentBody = z.object({ effectiveDate: zDate, rentColdCents: zCents.min(0).optional(), prepaymentOpsCents: zCents.min(0).optional(), prepaymentHeatCents: zCents.min(0).optional(), preview: z.boolean().default(true), reason: z.string().max(500).optional() });

/** Index values of an index rent (§ 557b): consumer price index for Germany, month as YYYY-MM. */
const zIndex = z.object({ baseMonth: z.string().regex(/^\d{4}-\d{2}$/), baseValue: z.number().positive().max(10000), currentMonth: z.string().regex(/^\d{4}-\d{2}$/), currentValue: z.number().positive().max(10000) });
/**
 * Miete / Vorauszahlungen anpassen ab Datum (packages/shared/src/rentChange.ts). `preview` returns amounts, checks and the
 * letter text without writing. Which amounts a kind may change: RENT_CHANGE_FIELDS.
 */
export const RentChangeBody = z
  .object({
    kind: z.enum(RENT_CHANGE_KINDS),
    effectiveDate: zDate,
    rentColdCents: zCents.min(0).optional(),
    prepaymentOpsCents: zCents.min(0).optional(),
    prepaymentHeatCents: zCents.min(0).optional(),
    /** Begründung (§ 558a: Mietspiegel, Vergleichswohnungen, Gutachten) or basis of the change; printed in the letter. */
    justification: z.string().trim().max(2000).optional(),
    /** Free text added to the letter. */
    note: z.string().trim().max(1000).optional(),
    index: zIndex.optional(),
    cappingPct: z.union([z.literal(15), z.literal(20)]).default(20),
    preview: z.boolean().default(true),
  })
  .superRefine((b, ctx) => {
    const f = RENT_CHANGE_FIELDS[b.kind];
    if (!f.rent && b.rentColdCents !== undefined) ctx.addIssue({ code: 'custom', path: ['rentColdCents'], message: 'Die Nettokaltmiete kann mit dieser Art nicht geändert werden' });
    if (!f.prepayments && (b.prepaymentOpsCents !== undefined || b.prepaymentHeatCents !== undefined)) ctx.addIssue({ code: 'custom', path: ['prepaymentOpsCents'], message: 'Die Vorauszahlungen können mit dieser Art nicht geändert werden' });
    if (b.kind === 'index_557b' && !b.index) ctx.addIssue({ code: 'custom', path: ['index'], message: 'Indexwerte fehlen' });
    if (!b.preview && b.kind === 'increase_558' && (b.justification ?? '').length < 10) ctx.addIssue({ code: 'custom', path: ['justification'], message: 'Bitte begründe die Mieterhöhung (§ 558a BGB)' });
  });
/** Answer of the tenants to a § 558 request: consent (optionally only to a lower rent, Teilzustimmung) or refusal. */
export const RentChangeConsentBody = z.object({ decision: z.enum(['given', 'refused']), date: zDate, agreedRentColdCents: zCents.min(0).optional(), note: z.string().trim().max(1000).optional() });
export const RentChangeDispatchBody = z.object({ channels: z.array(z.enum(RENT_CHANGE_CHANNELS)).min(1).max(4) });
export const RentChangeWithdrawBody = z.object({ reason: zReason });
/** Undo a recorded move-out (correction event, nothing is deleted): why, for the Chronik. */
export const CancelMoveOutBody = z.object({ reason: zReason });
export const DepositBody =z.object({ amountCents: zCents.positive(), type: z.enum(['cash', 'account', 'guarantee']), receivedOn: zDate, accountIban: z.string().max(34).optional() });
export const DepositSettlementBody = z.object({
  settledOn: zDate,
  interestCents: zCents.min(0).default(0),
  deductions: z.array(z.object({ label: z.string().min(1).max(120), amountCents: zCents.positive() })).default([]),
  /** Rückstand is deducted, Guthaben paid out with the deposit (docs/18 V5). */
  includeOpenBalance: z.boolean().default(true),
  /** Einbehalt for the expected Nebenkosten until the statement of the move-out year is final; omitted = suggestion. */
  retainCents: zCents.min(0).optional(),
  preview: z.boolean().default(true),
});
/** The payout of the settlement was transferred (ends the deadline "Kaution auszahlen"). */
export const DepositPayoutBody = z.object({ paidOutOn: zDate });
/** § 551 Abs. 2 BGB: the Barkaution is paid in three monthly instalments (3) or at once (1), docs/15 #34. */
export const DepositInstalmentsBody = z.object({ instalments: z.union([z.literal(1), z.literal(3)]) });
/** Release the Einbehalt after the final statement: offset the Nachzahlung, pay out the rest. */
export const DepositReleaseBody = z.object({ releasedOn: zDate, preview: z.boolean().default(true) });
